import * as React from 'react';
import {useEffect} from 'react';
import {useQuery} from '@tanstack/react-query';

import './style.scss';
import {PageModel} from "features/template/types";
import {StartPageViewModel} from 'features/template/viewModels/StartPage';
import {useAuth} from "features/authentication/hooks/useAuth";
import {getCurrentSite, getEnvSpecificUrl, getSiteSpecificConfig} from "features/services/utils/env-helper";
import { useDeviceStore, selectIsMobile } from 'features/ui/store/useDeviceStore';
import {SearchResult} from 'features/search/types';
import { useSearchStore } from 'features/search/store/useSearchStore';
import {TimeSeriesQueryMethods} from 'features/services/timeSeries/services/TimeSeriesService/query';
import classnames from 'classnames';
import {HeadlineLevel, Margin, ParagraphSize, SITE, TextColor} from "features/ui/enums";
import {StructuredData} from "@molecules/StructuredData";
import {CtaHeaderBlock} from "components/blocks/Headers/CtaHeaderBlock";
import {getImageUrlFromCDN} from "@atoms/Media/AsyncImage/helper";
import {ImageSizes} from '@organisms/Shell/image-size-enum';
import {MovingStill} from "@atoms/Media/MovingStill";
import {Headline} from "@atoms/Text/Headline";
import {P} from "@atoms/Text/P";
import {Search} from "@organisms/ResidenceSearch/Search";
import {SuggestedTags} from "@molecules/ResidenceSearchComponents/SuggestedTags";
import {SITE_URLS} from "features/search/constants";
import {SearchResultBlockV2} from "components/blocks/ProjectAndObject/SearchResultBlockV2";
import {ContentArea} from "@organisms/GeneralComponents/ContentArea";
import {AccessibleSlider} from '@molecules/SlidingContent/AccessibleSlider';
import {getFormattedMessage} from "features/services/utils/language-helper";
import {PromotionBlockGrid} from "@organisms/PromotionBlockGrid";
import {SelectedObjectsBlock} from "components/blocks/ProjectAndObject/SelectedObjectsBlock";
import {DisplayOptionSearchResultBlock} from "features/search/enums";
import {useNavigate} from "react-router";
import {buildNavigationUrlFromTags} from "features/search/helpers";
import { ModalPosition, ModalType, useModal } from 'features/modal/hooks/useModal';
import { ResidenceSearchModal } from 'components/modals/General/ResidenceSearchModal';
import { ResidenceCard, ResidenceCardType } from '@molecules/ResidentialCards/ResidenceCard';

type Props = PageModel<StartPageViewModel>;

export const StartPage: React.FunctionComponent<Props> = ({viewModel}) => {
    const {user} = useAuth();
    const navigate = useNavigate();

    const siteConfig = getSiteSpecificConfig();
    const currentSite = getCurrentSite();
    const isMobile = useDeviceStore(selectIsMobile);

    const { open } = useModal();
    const openSearchModal = () => {
        open(
            <ResidenceSearchModal />,
            {
                type: ModalType.DEFAULT,
                position: ModalPosition.DEFAULT,
                fullscreen: false,
                noHash: true,
                ariaLabel: getFormattedMessage('jm.components.ResidenceSearchModal.ariaLabel'),
            }
        );
    };

    const { data: lastVisited = [] } = useQuery({
        queryKey: ['timeseries', 'lastVisited', user],
        queryFn: () => TimeSeriesQueryMethods.getLastVisited(user),
        enabled: !!user,
    });

    useEffect(() => {
        const store = useSearchStore.getState();
        store.changeNavigationStatus(false);
        store.clearSearchTags();
        store.changeNavigationStatus(true);
    }, []);

    // Navigate to search when a tag is added on the start page
    useEffect(() => {
        const unsubscribe = useSearchStore.subscribe(
            (state) => state.filters.tags,
            (tags, prevTags) => {
                if (tags.length > 0 && tags.length > prevTags.length) {
                    const url = buildNavigationUrlFromTags(tags);
                    navigate(url);
                }
            },
            { fireImmediately: false }
        );
        return () => unsubscribe();
    }, [navigate]);

    if(!viewModel) return null;

    const searchClasses = classnames('start-page__search', {
        'start-page__search--video': viewModel.video && !isMobile,
        'start-page__search--advanced': siteConfig.isSearchAdvancedEnabled,
        'start-page__search--simple': siteConfig.isSearchSimpleEnabled,
    });
    const promoGridClasses = classnames('', {
        'promotion-block-grid--large-margin': (viewModel && viewModel.contentAreaVip && viewModel.contentAreaVip.length === 0),
    });

    return (
        <div className="start-page">
            <StructuredData structuredData={viewModel.structuredDataSearchBox}/>
            <StructuredData structuredData={viewModel.structuredDataCompanyInfo}/>

            {process.env.NODE_ENV === 'development' && new Date().getMonth() === 11 && //Easter egg
                <div className="christmas"><div className="christmas-item"></div><div className="christmas-item"></div><div className="christmas-item"></div></div>
            }

            {/* Header */}
            {viewModel.headerBlock
                ? <CtaHeaderBlock {...viewModel.headerBlock} />
                : <div className={searchClasses}>
                    {!isMobile && viewModel.video && viewModel.video.url
                        ? <div className="start-page__video-container">
                            <MovingStill
                                srcset={[{src: viewModel.video.url, type: "video/mp4"}]}
                                fullWidth
                                playing
                            />
                        </div>
                        : <div
                            className="start-page__image-container"
                            style={(viewModel.video && !isMobile)
                                ? {background: `none`}
                                : (viewModel.image && viewModel.image.url)
                                    ? {background: `url(${getImageUrlFromCDN(viewModel.image, isMobile ? ImageSizes.SIZE_1200 :  ImageSizes.SIZE_2560, false)}) center center no-repeat`}
                                    : {}}
                        />
                    }

                    <div className={
                        process.env.NODE_ENV === 'development' && (new Date().getMonth() === 5 && new Date().getMinutes() % 3 === 0)
                            ? "start-page__search__content rainbow"
                            : "start-page__search__content"
                    }>
                        <Headline 
                            level={HeadlineLevel.H1} 
                            margin={Margin.S} 
                            center
                            darkBg
                            className="start-page__title"
                        >
                            {viewModel.header}
                        </Headline>

                        <P
                            size={ParagraphSize.XL}
                            center
                            color={TextColor.INVERSE}
                            className="start-page__under-title"
                        >
                            {viewModel.intro}
                        </P>

                        {siteConfig.isSearchAdvancedEnabled &&
                            <div className="search-wrapper" data-test-id="start-page-search-wrapper">
                                <div className="search-input-wrapper">
                                    <Search
                                        hideTitle
                                        fullWidth
                                        onMobileTap={isMobile ? openSearchModal : undefined}
                                    />
                                </div>

                                { viewModel.suggestedTags && <SuggestedTags featuredTags={viewModel.suggestedTags} pageSuggestionTags={viewModel.pageSuggestionTags} /> }
                            </div>
                        }

                        {siteConfig.isSearchSimpleEnabled &&
                            <div className="start-page__finnish-tags">
                                <SuggestedTags featuredTags={viewModel.suggestedTags} pageSuggestionTags={viewModel.pageSuggestionTags} hideShowMore={!SITE_URLS.isSearchEnabled} />
                            </div>
                        }
                    </div>
                </div>
            }

            {/*
            <Container>
                <GeoLocationButton />
            </Container>
            */}

            {/* Suggested objects/projects */}
            {(currentSite !== SITE.BORATT && currentSite !== SITE.SENIORGARDEN) &&
                <div className="start-page__move-in-ready">
                    <SearchResultBlockV2
                        data={{
                            searchTypeArea: false,
                            searchTypeStage: true, 
                            searchTypeObject: false, 
                            displayOption: DisplayOptionSearchResultBlock.SLIDER,
                            searchUrl: `${getEnvSpecificUrl((getSiteSpecificConfig().siteId))}${SITE_URLS.searchUrl}?inhabitDate=Now&projectStatus=${currentSite === SITE.FI ? 'fd13208f-30dc-4d20-b0c8-957007a1e0e6' : 'f27aaa79-35dc-4817-b70f-c71750260d01%2Cfd13208f-30dc-4d20-b0c8-957007a1e0e6'}`,
                            searchLink: `${SITE_URLS.searchUrl}?inhabitDate=Now&projectStatus=${currentSite === SITE.FI ? 'fd13208f-30dc-4d20-b0c8-957007a1e0e6' : 'f27aaa79-35dc-4817-b70f-c71750260d01%2Cfd13208f-30dc-4d20-b0c8-957007a1e0e6'}`,
                            buttonText: getFormattedMessage('jm.components.StartPage.seeAll'),
                            title: getFormattedMessage('jm.components.StartPage.lastChanseHeader'),
                            content: getFormattedMessage('jm.components.StartPage.lastChanseText'),
                            onStartPage: true,
                        }} component={''} renderStyle={''}
                    />
                </div>
            }
            {((currentSite === SITE.BORATT || currentSite === SITE.SENIORGARDEN) && viewModel.selectedObjectsBlock) &&
                <div className="start-page__move-in-ready">
                    <SelectedObjectsBlock {...viewModel.selectedObjectsBlock} />
                </div>
            }

            {/* Promo grid */}
            {viewModel.contentAreaTop.length > 0 &&
                <PromotionBlockGrid
                    id="start-content-area-top"
                    className={promoGridClasses}
                    blocks={viewModel.contentAreaTop}
                />
            }

            {/* Vip promo */}
            {viewModel.contentAreaVip.length > 0 &&
                <ContentArea id="start-content-area-vip" blocks={viewModel.contentAreaVip}/>
            }

            {/* Last visited */}
            {lastVisited && lastVisited.length > 0 &&
                <div className="start-page__suggestions" data-test-id="start-page-suggestions">
                    <AccessibleSlider
                        key={`latest-visited`}
                        header={getFormattedMessage('jm.components.StartPage.latestVisitsHeader')}
                        text={getFormattedMessage('jm.components.StartPage.latestVisitsText')}
                        level={HeadlineLevel.H2}
                    >
                        {lastVisited.map((result: SearchResult) => {
                            if(!result) return null;

                            return (
                                <ResidenceCard
                                    residence={result} 
                                    key={`card-${result.id}`} 
                                    cardType={result.type === 'object' 
                                        ? ResidenceCardType.OBJECT
                                        : ResidenceCardType.PROJECT
                                    }
                                    horizontal={lastVisited.length === 1 && !isMobile}
                                    level={HeadlineLevel.H3}
                                />
                            );
                        })}
                    </AccessibleSlider>
                </div>
            }

            {/* Content area */}
            {viewModel.contentAreaBottom.length > 0 &&
                <ContentArea id="start-content-area-bottom" blocks={viewModel.contentAreaBottom}/>
            }
        </div>
    );
};
